import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Grid,
  Snackbar
} from '@mui/material';
import { AcUnit as FreezeIcon, CheckCircle as SuccessIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';

const PlanFreezeRequest = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [eligibility, setEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(true);
  const [formData, setFormData] = useState({
    requestedDays: 14,
    requestReason: ''
  });
  const [errors, setErrors] = useState({});
  const [previewDates, setPreviewDates] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (open) {
      checkEligibility();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (eligibility?.eligible && formData.requestedDays >= 14) {
      calculatePreviewDates();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.requestedDays, eligibility]);

  const checkEligibility = useCallback(async () => {
    setCheckingEligibility(true);
    try {
      const response = await api.get('/plan-freeze/eligibility');
      setEligibility(response.data.data);
    } catch (error) {
      console.error('Error checking freeze eligibility:', error);
      setEligibility({
        eligible: false,
        reason: error.response?.data?.error || t('planFreeze.unableToCheckEligibility')
      });
    } finally {
      setCheckingEligibility(false);
    }
  }, [t]);

  const calculatePreviewDates = useCallback(() => {
    if (!eligibility?.details?.membershipEndDate) return;

    const currentEndDate = new Date(eligibility.details.membershipEndDate);
    const freezeDays = parseInt(formData.requestedDays);
    const newEndDate = new Date(currentEndDate.getTime() + (freezeDays * 24 * 60 * 60 * 1000));

    const preview = {
      freezeStart: new Date(),
      freezeEnd: new Date(Date.now() + (freezeDays * 24 * 60 * 60 * 1000)),
      currentEndDate,
      newEndDate
    };

    if (eligibility.details.hasNextPlan) {
      // Next plan dates will also shift
      preview.hasNextPlan = true;
    }

    setPreviewDates(preview);
  }, [eligibility, formData.requestedDays]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.requestedDays || formData.requestedDays < 14) {
      newErrors.requestedDays = t('planFreeze.minDaysError');
    }

    if (formData.requestedDays > (eligibility?.remainingDays || 0)) {
      newErrors.requestedDays = t('planFreeze.durationHelper', { max: eligibility?.remainingDays });
    }

    if (formData.requestedDays > 365) {
      newErrors.requestedDays = t('planFreeze.maxDaysError');
    }

    if (!formData.requestReason.trim()) {
      newErrors.requestReason = t('planFreeze.reasonRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await api.post('/plan-freeze/request', {
        requestedDays: parseInt(formData.requestedDays),
        requestReason: formData.requestReason
      });

      setSuccessMessage(t('planFreeze.requestSubmitted', { days: formData.requestedDays }));

      if (onSuccess) {
        onSuccess();
      }

      // Close dialog after showing success
      setTimeout(() => {
        handleClose();
      }, 100);
    } catch (error) {
      console.error('Error submitting freeze request:', error);
      const errorMessage = error.response?.data?.error || t('planFreeze.failedToSubmit');
      setErrors({ submit: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({ requestedDays: 14, requestReason: '' });
    setErrors({});
    setPreviewDates(null);
    onClose();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FreezeIcon />
          {t('planFreeze.requestTitle')}
        </Box>
      </DialogTitle>

      <DialogContent>
        {checkingEligibility ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : !eligibility?.eligible ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body1" gutterBottom>
              <strong>{t('planFreeze.cannotRequestFreeze')}</strong>
            </Typography>
            <Typography variant="body2">
              {eligibility?.reason || t('planFreeze.notEligible')}
            </Typography>
          </Alert>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            {/* Eligibility Info */}
            <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
              <CardContent>
                <Typography variant="subtitle2" color="primary" gutterBottom>
                  Freeze Availability
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Plan
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {eligibility.details.planName}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Remaining Freeze Days
                    </Typography>
                    <Typography variant="body1" fontWeight="medium" color="primary">
                      {eligibility.remainingDays} days
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Freeze Duration Input */}
            <TextField
              fullWidth
              label={t('planFreeze.freezeDurationDays')}
              type="number"
              value={formData.requestedDays}
              onChange={(e) => setFormData({ ...formData, requestedDays: e.target.value })}
              error={!!errors.requestedDays}
              helperText={errors.requestedDays || t('planFreeze.durationHelper', { max: eligibility.remainingDays })}
              inputProps={{ min: 14, max: eligibility.remainingDays }}
              sx={{ mb: 3 }}
              required
            />

            {/* Reason Input */}
            <TextField
              fullWidth
              label={t('planFreeze.reasonLabel')}
              multiline
              rows={3}
              value={formData.requestReason}
              onChange={(e) => setFormData({ ...formData, requestReason: e.target.value })}
              error={!!errors.requestReason}
              helperText={errors.requestReason || t('planFreeze.reasonHelper')}
              sx={{ mb: 3 }}
              required
            />

            {/* Preview Dates */}
            {previewDates && (
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Freeze Period Preview
                  </Typography>
                  <Divider sx={{ my: 1 }} />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Freeze Period
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(previewDates.freezeStart)} → {formatDate(previewDates.freezeEnd)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Current Plan End Date
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(previewDates.currentEndDate)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      New Plan End Date (after freeze)
                    </Typography>
                    <Typography variant="body2" color="primary.main" fontWeight="medium">
                      {formatDate(previewDates.newEndDate)}
                    </Typography>
                  </Box>

                  {previewDates.hasNextPlan && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      Your next plan start date will also be adjusted to match the new end date.
                    </Alert>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Important Notice */}
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Important:</strong> During the freeze period:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                <li>You cannot make facility bookings</li>
                <li>You cannot join scheduled classes</li>
                <li>Your plan end date will be extended by the freeze duration</li>
                <li>This request requires admin approval</li>
              </Typography>
            </Alert>

            {errors.submit && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.submit}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('planFreeze.cancel')}
        </Button>
        {eligibility?.eligible && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !formData.requestReason.trim()}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {loading ? t('planFreeze.submitting') : t('planFreeze.submitRequest')}
          </Button>
        )}
      </DialogActions>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          icon={<SuccessIcon />}
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default PlanFreezeRequest;
